import type { ITarget } from './ITarget'
import type { IAddress } from './IAddress'
import type { IFile } from './IFiles'
import type { WeekDay } from './IDate'
import type { InventoryAccountingType } from './IInventory'

export interface IServiceResource {
  type: 'INVENTORY'
  accountingType?: InventoryAccountingType
  quantityTotal: number
}

export enum ServiceTypes {
  Rent = 'rent',
  Individual = 'individual',
  GroupService = 'groupService',
  Additional = 'additional',
  Inventory = 'inventoryRent',
}

export enum ServiceFormats {
  time = 'time',
  timeAndPlace = 'timeAndPlace',
  without = 'without',
  Period = 'PERIOD',
}

export enum ServiceOfferMode {
  Both = 'BOTH',
  Main = 'MAIN',
  Addon = 'ADDON',
}

export enum ServicePrepayment {
  Without = 'without',
  Main = 'main',
  MainAndAdditional = 'mainAndAdditional',
}

export enum ServiceAmountTypes {
  Percent = 'percent',
  Amount = 'amount',
}

export enum OrderDiscountTypes {
  Percent = 'percent',
  Amount = 'amount',
}

export interface IPriceSettingParamSmall {
  amount: string | number
  amountType: ServiceAmountTypes
}

export interface IPriceSettingParam extends IPriceSettingParamSmall {
  weekDays: string[]
  timeStart: number
  timeEnd: number
  durationHour?: string | number
  durationMinutes?: string | number
  amountPeople: string | number
  isDisabled: boolean
  sortPosition?: number
}

export interface IServicePrepaymentParams {
  id?: number | string
  weekDays: WeekDay[]
  /** минуты от начала суток */
  timeStart: number
  timeEnd: number
  amount: number | string
  amountType: ServiceAmountTypes
  sortPosition?: number
}

export interface IServiceAdditionalService extends IService {
  id: number
  isRequire: boolean
  customerCanView: boolean
}

export interface IServicePriceSettingsParam {
  id?: string | number
  weekDays: WeekDay[]
  timeStart: number
  timeEnd: number
  durationHour?: string | number
  durationMinutes?: string | number
  amountPeople: string | number
  quantity?: string | number
  amount: string | number
  isDisabled: boolean
  sortPosition?: number
}

export enum ServicePriceSettingsActions {
  Const = 'const',
  Temporarily = 'temporarily',
  From = 'from',
}

export interface IServicePriceSettings {
  id?: string | number
  target?: ITarget
  unimportantTarget: boolean
  action: ServicePriceSettingsActions
  dateStart?: Date | string
  dateEnd?: Date | string
  parameters: IServicePriceSettingsParam[]
  sortPosition?: number
}

export enum ServiceBlockBookingTypes {
  M = 'min',
  H = 'hour',
}

export enum ServiceSeatsPaymentTypes {
  Place = 'place',
  Time = 'time',
}

export enum ServiceAdditionalTypes {
  Requisite = 'requisite',
  Process = 'process',
}

export enum ServicePaymentTypes {
  Time = 'time',
  Piece = 'piece',
}

export interface IAdditionalEnableServices {
  readonly addonType?: 'ADDITIONAL' | 'INVENTORY_RENT'
  id: number
  isEnable: boolean
  isRequire: boolean
  customerCanView: boolean
  childrenId: number
  parentId: number
  price?: number | null
}

export interface IService {
  id: number
  resources?: IServiceResource[]
  title: string
  receiptTitle?: string
  description?: string
  price: string | number
  priceSettings: IServicePriceSettings[]
  addresses?: IAddress[]
  capacity?: boolean
  type: ServiceTypes
  offerMode?: ServiceOfferMode
  additionalType: ServiceAdditionalTypes
  files?: IFile[]
  format: ServiceFormats
  minTimeLength: string | number
  timeLength: string | number
  breakIsEnable: boolean
  break: string | number
  blockBookingIsEnable: boolean
  blockBookingTime: string | number
  blockBookingType: ServiceBlockBookingTypes
  seats: string | number
  includedSeats: string | number
  additionalSeatsIsEnable: boolean
  additionalSeats: string | number
  additionalSeatsPaymentType: ServiceSeatsPaymentTypes
  additionalSeatsAmount: string | number
  availableBookingHorizon?: number
  targets?: ITarget[]
  amountPeopleMin: string | number
  amountPeopleMax: string | number
  paymentType: ServicePaymentTypes
  paymentAmount: string | number
  duration: string | number
  parentServices?: Array<{ id: number }>
  customerCanView: boolean
  isEnable: boolean
  isRequire: boolean
  isValid: boolean // Черновик. Флаг завершенности создания услуги
  clientId: number
  categoryId?: number | null
  sortPosition?: number
  additionalServices: IServiceAdditionalService[]
  prepayment: ServicePrepayment
  prepaymentAmount: string | number
  prepaymentType: ServiceAmountTypes
  prepaymentParams?: IServicePrepaymentParams[]
  startTimeOffset?: number
  paymentLinkSeconds?: number | null
  nearBookingPaymentLinkEnabled?: boolean | null
  nearBookingThresholdSeconds?: number | null
  nearBookingPaymentLinkSeconds?: number | null
  additionalEnableServices?: IAdditionalEnableServices[]
}

export type IServiceAdditionalFields = Pick<
  IService,
  'id' | 'type' | 'title' | 'description' | 'additionalType' | 'paymentType' | 'paymentAmount' | 'price' | 'duration'
>

export interface IServiceSmall extends Pick<IService, 'id' | 'title' | 'type' | 'isEnable' | 'isValid'> {
  sortPosition: number
}
